import { bind, trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';
import { getRte } from '../../facades/editor';
import window from 'window-or-global';
import { getOverride } from '@confluence/meta';
import MacroUtils from '../utils/macro-utils';
import { TABLE_CONTAINER_FILTER } from '../utils/commons';
// TODO: remove jquery from here and from webpack config once CONFDEV-52616 is done.
// eslint-disable-next-line no-restricted-imports
import $ from 'jquery';

const WikiAutoformat = function (jQueryEvent, data) {
	let rangeAfterAutoFormat;

	if (
		getOverride('atlassian-account-id') &&
		getOverride('confluence.prefs.editor.disable.autoformat')
	) {
		return; //autoformat enabled by default and for anon-users
	}

	function encodeDomNode(domNode) {
		return $('<div>').append(domNode.cloneNode(true)).html();
	}

	function getStartContainer(lengthOfMatch, currentTextNode, currentOffset) {
		let siblingParent;
		let siblingIndex;
		if (!currentTextNode) {
			// text node is null
			return;
		}
		if (currentTextNode.nodeType !== 3) {
			currentTextNode = currentTextNode.childNodes[currentOffset - 1];
			currentOffset = currentTextNode.length;
		}

		for (
			let ps = currentTextNode, runningOffset = currentOffset;
			ps && ps.nodeType === 3;
			ps = ps.previousSibling
		) {
			if (runningOffset == -1) {
				runningOffset = ps.nodeValue.length;
			}

			if (runningOffset > lengthOfMatch) {
				return {
					container: ps,
					offset: runningOffset - lengthOfMatch,
				};
			} else if (runningOffset == lengthOfMatch) {
				siblingIndex = 0;
				siblingParent = ps.parentNode;
				while ((ps = ps.previousSibling)) {
					siblingIndex++;
				}
				return {
					container: siblingParent,
					offset: siblingIndex,
				};
			} else {
				lengthOfMatch -= runningOffset;
				runningOffset = -1;
			}
		}

		return null;
	}

	/**
	 * Assuming | is the caret marker the caret could be positioned as such:
	 * <p><textNode>content of text node|</textNode></p>
	 *                                  ^
	 * or (less obviously) as such:
	 * <p><textNode>content of text node</textNode>|</p>
	 *                                             ^
	 */
	function getTextFromPreviousSiblingTextNodes(range) {
		let currentNode;
		let currentOffset;
		if (!range || !range.collapsed) {
			throw new Error('range is null or not collapsed');
		}
		currentNode = range.startContainer;
		currentOffset = range.startOffset;

		if (currentNode.nodeType === 1 && currentOffset > 0) {
			//descend into the relevent child node
			currentNode = currentNode.childNodes[range.startOffset - 1];
			if (currentNode.nodeType === 3) {
				//It's a text node so we'll use it.
				currentOffset = currentNode.nodeValue.length;
			} else {
				//We're working under the assumption that the start container will never be
				// higher than textnode.parentNode.
				return '';
			}
		} else if (currentNode.nodeType !== 3) {
			//not a text node or element, we can assume "".
			return '';
		}

		let result = currentNode.nodeValue.substring(0, currentOffset);

		for (let ps = currentNode.previousSibling; ps && ps.nodeType === 3; ps = ps.previousSibling) {
			result = ps.nodeValue + result;
		}

		return result;
	}

	function createHandler(regex, fragmentCreator, swallowTriggerChar, containerFilter) {
		return {
			handles(ed) {
				let result = false;
				const range = ed.selection.getRng(true);
				const candidateTextNode = range.commonAncestorContainer || {};

				if (!range.collapsed) {
					return false;
				}

				// CONFDEV-4376: Autoformat should be disabled in preformatted text.
				if ($(candidateTextNode).closest('pre,.text-placeholder').length) {
					return false;
				}

				if (containerFilter && $(candidateTextNode).closest(containerFilter).length) {
					return false;
				}

				result = regex.test(getTextFromPreviousSiblingTextNodes(range));

				return result;
			},
			execute(ed, nativeEvent, jQueryEvent) {
				let combinedText;
				let matchGroupsOffset = 1;
				const charCode = getCharCode(nativeEvent);

				if (charCode === 32) {
					ed.execCommand('mceInsertContent', false, '&nbsp;'); //spaces don't stick around for undos (it's tragic, I know)
				} else {
					ed.execCommand('mceInsertContent', false, String.fromCharCode(charCode));
				}

				// Wrap the replacement operation in a single undo transaction
				ed.undoManager.transact(() => {
					const range = ed.selection.getRng(true);
					combinedText = getTextFromPreviousSiblingTextNodes(range);

					//CONFDEV-4771 Just for tables, add a space at the end and change the offset for matchGroups
					//This fixes and IE8 issue where the table autoformat didn't always work
					if (combinedText[combinedText.length - 1] == '|') {
						combinedText += ' ';
						matchGroupsOffset = 0;
					}

					const matchGroups = regex.exec(combinedText.substring(0, combinedText.length - 1)); // regexes need to work for handles() where trigger character has not been appended
					const startContainerData = getStartContainer(
						matchGroups[1].length + matchGroupsOffset,
						range.commonAncestorContainer,
						range.startOffset,
					);
					range.setStart(startContainerData.container, startContainerData.offset);
					const commonAncestor = $(range.commonAncestorContainer);
					ed.selection.setRng(range); //we have to set the editors selection now that we have modified the range to have text selected

					if (
						commonAncestor.closest('.wysiwyg-macro-body').length &&
						range.toString() == commonAncestor.text()
					) {
						//if the entire text of container node is selected and we're in a macro, don't let let the macro get deleted
						commonAncestor[0].innerHTML = '<br>';
						ed.selection.select(commonAncestor[0].childNodes[0]);
						ed.selection.collapse(true); //mceInsertContent throws an exception when only <br> is selected
					} else {
						//else delete the selection since the area will be kept cursor targetable by whatever else is there.
						ed.execCommand('delete', false, {}, { skip_undo: true });
					}

					fragmentCreator(matchGroups, ed.selection.getRng(true));
					rangeAfterAutoFormat = ed.selection.getRng(true);
				});

				if (swallowTriggerChar) {
					jQueryEvent.preventDefault();
					jQueryEvent.stopPropagation();
					window.tinymce.dom.Event.cancel(nativeEvent);
					// CONFDEV-2503 - cancelling the event stops the browser from scrolling to the new content
					// so manually scroll there.
					getRte().showElement(rangeAfterAutoFormat.startContainer);
					return false;
				}
			},
		};
	}

	function getCharCode(nativeEvent) {
		return nativeEvent.which;
	}

	function HandlerManager() {
		this.handlers = {};
	}

	HandlerManager.prototype = {
		registerHandler(triggerCharCode, handler) {
			if (!this.handlers[triggerCharCode]) {
				this.handlers[triggerCharCode] = [];
			}

			this.handlers[triggerCharCode].push(handler);
		},
		executeHandlers(triggerCharCode, ed, nativeEvent, jQueryEvent) {
			let result = true;
			$.each(this.handlers[triggerCharCode] || [], (i, handler) => {
				if (handler.handles(ed)) {
					result = handler.execute(ed, nativeEvent, jQueryEvent);
					return false; // signal end of iteration
				}
			});

			return result;
		},
	};

	const ed = data.editor;
	const handlerManager = new HandlerManager();

	/* Emoticons */

	/** Create an Emoticon object and register it with the handlerManager */
	function Emoticon(triggerChar, handlerRegex, emoticon, title, image) {
		const triggerCharCode = triggerChar.charCodeAt(0);
		const imagePath = `${getRte().getResourceUrlPrefix()}/images/icons/emoticons/${image}`;

		const handler = createHandler(
			handlerRegex,
			() => {
				const img = ed.dom.createHTML('img', {
					src: imagePath,
					alt: ed.translate(`tinymce.${title}`),
					title: ed.translate(`tinymce.${title}`),
					border: 0,
					class: `emoticon emoticon-${emoticon}`,
					'data-emoticon-name': emoticon,
				});
				ed.execCommand('mceInsertContent', false, img, { skip_undo: true });
			},
			true,
		);
		this.imagePath = imagePath;
		handlerManager.registerHandler(triggerCharCode, handler);
	}

	const emoticons = [
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.SMILE,
			'smile',
			'emotions_dlg.smile',
			'smile.png',
		),
		new Emoticon(
			'(',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.SAD,
			'sad',
			'emotions_dlg.sad',
			'sad.png',
		),
		new Emoticon(
			'P',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.CHEEKY,
			'cheeky',
			'emotions_dlg.tongue',
			'tongue.png',
		),
		new Emoticon(
			'p',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.CHEEKY_2,
			'cheeky',
			'emotions_dlg.tongue',
			'tongue.png',
		),
		new Emoticon(
			'D',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.LAUGH,
			'laugh',
			'emotions_dlg.biggrin',
			'biggrin.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.WINK,
			'wink',
			'emotions_dlg.wink',
			'wink.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.THUMBS_UP,
			'thumbs-up',
			'emotions_dlg.thumbs_up',
			'thumbs_up.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.THUMBS_DOWN,
			'thumbs-down',
			'emotions_dlg.thumbs_down',
			'thumbs_down.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.INFORMATION,
			'information',
			'emotions_dlg.information',
			'information.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.TICK,
			'tick',
			'emotions_dlg.check',
			'check.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.CROSS,
			'cross',
			'emotions_dlg.error',
			'error.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.WARNING,
			'warning',
			'emotions_dlg.warning',
			'warning.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.PLUS,
			'plus',
			'emotions_dlg.add',
			'add.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.MINUS,
			'minus',
			'emotions_dlg.forbidden',
			'forbidden.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.QUESTION,
			'question',
			'emotions_dlg.help_16',
			'help_16.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.LIGHT_ON,
			'light-on',
			'emotions_dlg.lightbulb_on',
			'lightbulb_on.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.LIGHT_OFF,
			'light-off',
			'emotions_dlg.lightbulb',
			'lightbulb.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.YELLOW_STAR,
			'yellow-star',
			'emotions_dlg.star_yellow',
			'star_yellow.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.YELLOW_STAR_2,
			'yellow-star',
			'emotions_dlg.star_yellow',
			'star_yellow.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.RED_STAR,
			'red-star',
			'emotions_dlg.star_red',
			'star_red.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.GREEN_STAR,
			'green-star',
			'emotions_dlg.star_green',
			'star_green.png',
		),
		new Emoticon(
			')',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.BLUE_STAR,
			'blue-star',
			'emotions_dlg.star_blue',
			'star_blue.png',
		),
		new Emoticon(
			'3',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.HEART,
			'heart',
			'emotions_dlg.heart',
			'heart.png',
		),
		new Emoticon(
			'3',
			WikiAutoformat.regularExpressions._REGEXES_EMOTICON.BROKEN_HEAR,
			'broken-heart',
			'emotions_dlg.broken_heart',
			'broken_heart.png',
		),
	];

	let i;

	// Preload the emoticon images for faster auto-complete response
	const emoticonImages = [];
	for (i = 0; i < emoticons.length; i++) {
		emoticonImages[i] = new window.Image();
		emoticonImages[i].src = emoticons[i].imagePath;
	}
	/* End Emoticons */
	function formatInlineText(inlineFormat, content) {
		const format = ed.formatter.get(inlineFormat)[0];
		const domNode = ed.dom.create(format.inline, { style: format.styles });
		domNode.appendChild(document.createTextNode(content));

		// Insert the content with a trailing zero-width space to ensure correct cursor positioning.
		ed.execCommand('mceInsertContent', false, `${encodeDomNode(domNode)}\uFEFF`, {
			skip_undo: true,
		});
	}
	// Definitions for markdown handlers with single character triggers
	const handlerFormatMap = {
		'*': 'italic',
		_: 'italic',
		'`': 'monospace',
	};

	$.each(handlerFormatMap, (handler, format) => {
		const regex = new RegExp(
			`(?:[\\s\\xA0\\u200b\\uFEFF]+|^)(\\${handler}(?=[^\\s${handler}])([^${handler}]*?[^\\s]))$`,
		); //\\xA0 is &nbsp; which is used in ie to create cursor targets
		handlerManager.registerHandler(
			handler.charCodeAt(0),
			createHandler(
				regex,
				(matchGroups) => {
					// require a space before asterisk to handle people using asterisk to denote footnotes
					formatInlineText(format, matchGroups[2]);
				},
				true,
			),
		);
	});

	// Definitions for markdown handlers with double character triggers
	const doubleCharHandlerFormatMap = {
		'*': 'bold',
		_: 'bold',
		'~': 'strikethrough',
	};

	$.each(doubleCharHandlerFormatMap, (handler, format) => {
		const regex = new RegExp(
			`(?:[\\s\\xA0\\u200b\\uFEFF]+|^)(\\${handler}\\${handler}(?=[^\\s])([^*]*?[^\\s])\\${handler})$`,
		);
		handlerManager.registerHandler(
			handler.charCodeAt(0),
			createHandler(
				regex,
				(matchGroups) => {
					formatInlineText(format, matchGroups[2]);
				},
				true,
			),
		);
	});

	// Heading handlers # -> ######
	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\#)$/,
			() => {
				ed.execCommand('formatBlock', false, 'h1', { skip_undo: true });
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\#\#)$/,
			() => {
				ed.execCommand('formatBlock', false, 'h2', { skip_undo: true });
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\#\#\#)$/,
			() => {
				ed.execCommand('formatBlock', false, 'h3', { skip_undo: true });
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\#\#\#\#)$/,
			() => {
				ed.execCommand('formatBlock', false, 'h4', { skip_undo: true });
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\#\#\#\#\#)$/,
			() => {
				ed.execCommand('formatBlock', false, 'h5', { skip_undo: true });
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\#\#\#\#\#\#)$/,
			() => {
				ed.execCommand('formatBlock', false, 'h6', { skip_undo: true });
			},
			true,
		),
	);

	// Handler for list shortcut 1)
	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(1\))$/,
			() => {
				ed.execCommand('InsertOrderedList');
			},
			true,
		),
	);

	// Handler for blockquote >
	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(>)$/,
			() => {
				ed.execCommand('formatBlock', false, 'blockquote', {
					skip_undo: true,
				});
			},
			true,
		),
	);

	// Handler for <hr> ---
	const hrHandlerDash = createHandler(
		/^\u200b?(\-\-)$/,
		() => {
			ed.execCommand('mceInsertContent', false, '<hr />', {
				skip_undo: true,
			});
		},
		true,
	);

	// Handler for <hr> ***
	const hrHandlerStar = createHandler(
		/^(\*\*)$/,
		() => {
			ed.execCommand('mceInsertContent', false, '<hr />', {
				skip_undo: true,
			});
		},
		true,
	);

	handlerManager.registerHandler('-'.charCodeAt(0), hrHandlerDash);
	handlerManager.registerHandler('*'.charCodeAt(0), hrHandlerStar);

	// Handler for Code Macro ```
	const codeMacroHandler = createHandler(
		/^\u200b?(\`\`)$/,
		() => {
			MacroUtils.insertMacro({
				contentId: getOverride('content-id') || '0',
				macro: {
					name: 'code',
					body: '',
				},
			});
		},
		true,
	);

	handlerManager.registerHandler('`'.charCodeAt(0), codeMacroHandler);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\*)$/,
			() => {
				ed.execCommand('InsertUnorderedList');
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(1\.)$/,
			() => {
				ed.execCommand('InsertOrderedList');
			},
			true,
		),
	);

	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/^\u200b?(\-)$/,
			() => {
				const dom = ed.dom;
				ed.execCommand('InsertUnorderedList');
				const list = dom.getParent(ed.selection.getNode(), 'OL,UL');
				if (list) {
					dom.setStyles(list, { listStyleType: 'square' });
					list.removeAttribute('data-mce-style');
				}
			},
			true,
		),
	);
	trigger('confluence.editor.registerHandlers', {
		handlerManager,
		createHandler,
		ed,
	});
	/**
	 * Handle '->' to be an arrow for left and right or both.
	 */

	const bothArrowHandler = createHandler(
		/((\<\-\-?\>)([^\s-]*))$/,
		(matchGroups) => {
			const bothArrow = '\u2194';
			ed.execCommand('mceInsertContent', false, bothArrow + matchGroups[3], {
				skip_undo: true,
			});
		},
		false,
	);
	handlerManager.registerHandler(' '.charCodeAt(0), bothArrowHandler);
	handlerManager.registerHandler(13, bothArrowHandler);

	const rightArrowHandler = createHandler(
		/((\-\-?\>)([^\s-]*))$/,
		(matchGroups) => {
			const rightArrow = '\u2192';
			ed.execCommand('mceInsertContent', false, rightArrow + matchGroups[3], {
				skip_undo: true,
			});
		},
		false,
	);
	handlerManager.registerHandler(' '.charCodeAt(0), rightArrowHandler);
	handlerManager.registerHandler(13, rightArrowHandler);

	const leftArrowHandler = createHandler(
		/((\<\-\-?)([^\s-]*))$/,
		(matchGroups) => {
			const leftArrow = '\u2190';
			ed.execCommand('mceInsertContent', false, leftArrow + matchGroups[3], {
				skip_undo: true,
			});
		},
		false,
	);
	handlerManager.registerHandler(' '.charCodeAt(0), leftArrowHandler);
	handlerManager.registerHandler(13, leftArrowHandler);

	/**
	 * Handle em and en dashes where there are surrounding spaces
	 */
	handlerManager.registerHandler(
		' '.charCodeAt(0),
		createHandler(
			/[^-]*[\s](\-\-\-?)$/,
			(matchGroups) => {
				const dash = matchGroups[1].length === 2 ? '\u2013' : '\u2014';
				ed.execCommand('mceInsertContent', false, dash, { skip_undo: true });
			},
			false,
		),
	);
	/**
	 * Handle em and en dashes with words on either side of the dashes. Delay the autoformat until a space or enter is
	 * pressed after the last word has been typed. That is, for 'foo--bar', only trigger when a space or enter is
	 * pressed after 'bar'.
	 */
	const dashHandler = createHandler(
		/(([^\s-]+)(\-\-\-?)([^\s-]+))$/,
		(matchGroups) => {
			const dash = matchGroups[3].length === 2 ? '\u2013' : '\u2014';
			ed.execCommand('mceInsertContent', false, matchGroups[2] + dash + matchGroups[4], {
				skip_undo: true,
			});
		},
		false,
	);
	handlerManager.registerHandler(' '.charCodeAt(0), dashHandler);
	handlerManager.registerHandler(13, dashHandler);

	/**
	 *  Table matching code
	 */
	handlerManager.registerHandler(
		13,
		createHandler(
			/(^\u200b?\|\|\s*(?:[^|]*\s?\|\|\s?)+$)/,
			(matchGroups) => {
				let tableMarkup = '<table class="confluenceTable"><tr>';
				let tableRows = '';
				let cellsEmpty = true;
				const cellWords = $(matchGroups[1].slice(2, -2).split('||')).map(function (cellWord) {
					cellWord = $.trim(this);
					cellsEmpty = cellsEmpty && cellWord == '';
					return cellWord;
				});
				if (cellsEmpty) {
					cellWords[0] = ed.translate('editor.autoformat.sampletext.firstcell');
				}

				for (let k = 0, l = cellWords.length; k < l; k++) {
					tableMarkup += `<th class="confluenceTh">${cellWords[k]}</th>`;
					tableRows += `<td class="confluenceTd">${getRte().ZERO_WIDTH_WHITESPACE}</td>`;
				}
				tableMarkup += `</tr><tr>${tableRows}</tr></table>`;
				ed.execCommand('mceInsertContent', false, tableMarkup, {
					skip_undo: true,
				});
				ed.selection.select(
					$(ed.selection.getRng(true).commonAncestorContainer)
						.parents('table')
						.find(cellsEmpty ? 'th' : 'td')[0].childNodes[0],
				);
				$(ed.selection.getRng().startContainer)
					.parent()
					.closest('[contenteditable="true"]')
					.focus();
			},
			true,
			TABLE_CONTAINER_FILTER,
		),
	);

	handlerManager.registerHandler(
		13,
		createHandler(
			/(^\u200b?\|\s?(?:[^|]*\s?\|\s?)+$)/,
			(matchGroups) => {
				let tableMarkup = '<table class="confluenceTable"><tr>';
				let cellsEmpty = true;
				const cellWords = $(matchGroups[1].slice(1, -1).split('|')).map(function (cellWord) {
					cellWord = $.trim(this);
					cellsEmpty = cellsEmpty && cellWord == '';
					return cellWord;
				});
				if (cellsEmpty) {
					cellWords[0] = ed.translate('editor.autoformat.sampletext.firstcell');
				}
				for (let k = 0, l = cellWords.length; k < l; k++) {
					tableMarkup += `<td class="confluenceTd">${cellWords[k]}</td>`;
				}
				tableMarkup += '</tr></table>';
				ed.execCommand('mceInsertContent', false, tableMarkup, {
					skip_undo: true,
				});
				cellsEmpty &&
					ed.selection.select(
						$(ed.selection.getRng(true).commonAncestorContainer).parents('table').find('td')[0]
							.childNodes[0],
					);
				$(ed.selection.getRng().startContainer)
					.parent()
					.closest('[contenteditable="true"]')
					.focus();
			},
			true,
			TABLE_CONTAINER_FILTER,
		),
	);
	/**
	 *  END - Table matching code
	 */

	const urlHandler = createHandler(
		/\b(((https?|ftp):\/\/|(www\.))[\w\.\$\-_\+!\*'\(\),/\?:@=&%#~;\[\]]+)$/,
		(matchGroups) => {
			//if a protocol is not provided, add http:// to the url.
			const url = matchGroups[3] ? matchGroups[1] : `http://${matchGroups[1]}`;
			const domNode = ed.dom.create('a', { href: url });
			domNode.appendChild(document.createTextNode(matchGroups[1]));
			ed.execCommand('mceInsertContent', false, encodeDomNode(domNode), {
				skip_undo: true,
			});
			ed.getDoc().execCommand('unlink', false, {});
		},
		false,
		'a',
	);
	handlerManager.registerHandler(' '.charCodeAt(0), urlHandler);
	handlerManager.registerHandler(13, urlHandler);

	const emailHandler = createHandler(
		/*eslint-disable max-len*/
		/\b((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)$/i,
		/*eslint-enable max-len*/
		(matchGroups) => {
			const domNode = ed.dom.create('a', { href: `mailto:${matchGroups[1]}` });
			domNode.appendChild(document.createTextNode(matchGroups[1]));
			ed.execCommand('mceInsertContent', false, encodeDomNode(domNode), {
				skip_undo: true,
			});
			ed.getDoc().execCommand('unlink', false, {});
		},
		false,
		'a',
	);
	handlerManager.registerHandler(' '.charCodeAt(0), emailHandler);
	handlerManager.registerHandler(13, emailHandler);

	const addQuickCorrect = function (regexLiteral, replacement) {
		const handler = createHandler(
			regexLiteral,
			() => {
				ed.execCommand('mceInsertContent', false, replacement, {
					skip_undo: true,
				});
			},
			false,
		);
		handlerManager.registerHandler(' '.charCodeAt(0), handler);
		handlerManager.registerHandler(13, handler);
	};

	addQuickCorrect(/(?:\b|^)(jira|JIRA)$/, 'Jira');
	addQuickCorrect(/(?:\b|^)([Ss]tatus[Pp]age)$/, 'Statuspage');
	addQuickCorrect(/(?:\b|^)([Ff]ishEye)$/, 'Fisheye');
	addQuickCorrect(/(?:\b|^)([Ss]ource[Tt]ree)$/, 'Sourcetree');
	addQuickCorrect(/(?:\b|^)([Hh]ip[Cc]hat)$/, 'Hipchat');
	addQuickCorrect(/(?:\b|^)([Bb]it[Bb]ucket)$/, 'Bitbucket');
	addQuickCorrect(/(?:\b|^)(atlassian)$/, 'Atlassian');

	const WIKI_TRIGGER_TO_REGEX = {
		']': WikiAutoformat.regularExpressions._REGEXES.WIKI_LINK,
		'}': WikiAutoformat.regularExpressions._REGEXES.WIKI_MACRO,
		'!': WikiAutoformat.regularExpressions._REGEXES.WIKI_EMBED,
	};
	$.each(WIKI_TRIGGER_TO_REGEX, (trigger, regex) => {
		handlerManager.registerHandler(
			trigger.charCodeAt(0),
			createHandler(
				regex,
				(matchGroups) => {
					const wiki = matchGroups[1] + trigger;
					const request = {
						type: 'POST',
						contentType: 'application/json; charset=utf-8',
						url: `${getOverride('context-path')}/rest/tinymce/1/wikixhtmlconverter`,
						data: JSON.stringify({
							wiki,
							entityId: getOverride('content-id'),
							spaceKey: getOverride('space-key'),
							suppressFirstParagraph: true,
						}),
						dataType: 'text', // if this is switched back to json be sure to use 'text json'. See CONFDEV-4799 for details.
						timeout: 5000,
					};

					if (trigger == '}') {
						//TODO
						window.tinymce.confluence.MacroUtils.insertMacro(request);
					} else if (trigger == '!') {
						$.ajax(request).done((data) => {
							if (!wiki === data) {
								window.tinymce.confluence.ImageUtils.insertImagePlaceholder(data);
							} else {
								// insertImagePlaceholder doesn't handle unchanged text
								ed.execCommand('mceInsertContent', false, data, {
									skip_undo: true,
								});
							}
						});
					} else {
						$.ajax(request).done((data) => {
							/**
							 * Skip undo here because the editor at this point is not in a state that we want to 'undo' to.
							 * (specifically, the editor has just had the wiki text the user just typed in removed - in
							 * preparation for the insertion of the actual markup returned by the server.
							 */
							ed.execCommand('mceInsertContent', false, data, {
								skip_undo: true,
							});
						});
					}
				},
				true,
			),
		);
	});

	ed.on('KeyPress', (nativeEvent) => {
		return handlerManager.executeHandlers(getCharCode(nativeEvent), ed, nativeEvent, jQueryEvent);
	});

	ed.on(
		'KeyDown',
		(nativeEvent) => {
			if (getCharCode(nativeEvent) === 13) {
				return handlerManager.executeHandlers(13, ed, nativeEvent, jQueryEvent);
			}
		},
		true,
	);

	// Helper function for testing.
	const getHandlerCount = () => {
		return Object.keys(handlerManager.handlers).length;
	};

	return { getHandlerCount };
};

WikiAutoformat.regularExpressions = {
	/**
	 * An internal map of regexes. Expose for testing.
	 */
	_REGEXES: {
		WIKI_MACRO: /(?:\s|^)(\{[^{^}]+)$/,
		WIKI_LINK: /(?:\s|^)(\[[^\[^\]]+)$/,
		WIKI_EMBED: /(?:\s|^)(![^!]{5,})$/, // Wiki renderer says image names have to be at least 5 chars long
	},

	_REGEXES_EMOTICON: {
		SMILE: /\B(:-?)$/,
		SAD: /\B(:-?)$/,
		CHEEKY: /\B(:-?)$/,
		CHEEKY_2: /\B(:-?)$/,
		LAUGH: /\B(:-?)$/,
		WINK: /\B(;-?)$/,
		THUMBS_UP: /\B(\(y)$/,
		THUMBS_DOWN: /\B(\(n)$/,
		INFORMATION: /\B(\(i)$/,
		TICK: /\B(\(\/)$/,
		CROSS: /\B(\(x)$/,
		WARNING: /\B(\(!)$/,
		PLUS: /\B(\(\+)$/,
		MINUS: /\B(\(-)$/,
		QUESTION: /\B(\(\?)$/,
		LIGHT_ON: /\B(\(on)$/,
		LIGHT_OFF: /\B(\(off)$/,
		YELLOW_STAR: /\B(\(\*)$/,
		YELLOW_STAR_2: /\B(\(\*y)$/,
		RED_STAR: /\B(\(\*r)$/,
		GREEN_STAR: /\B(\(\*g)$/,
		BLUE_STAR: /\B(\(\*b)$/,
		HEART: /\B(<)$/,
		BROKEN_HEAR: /\B(<\/)$/,
	},
};

export const regularExpressions = WikiAutoformat.regularExpressions;
export const plugin = WikiAutoformat;
export default () => {
	bind('init.rte', WikiAutoformat);
};
